import {filter, interval, map, Observable, ReplaySubject, share, Subject, switchMap, takeUntil} from "rxjs";
import {TIME_TO_PING, TIME_TO_PONG} from "./constants";
import {RxjsWebsocketWrapper} from "./rxjs-websocket-wrapper";
import {GetWebSocketResponses} from "rxjs-websockets";

export class WebsocketConnection {
  private connectionKey?: string;

  private constructor(
    private readonly _input$: ReplaySubject<string>,
    private readonly _receive$: Observable<string>,
    private readonly _disconnect$: Subject<void>,
    readonly lastConnectionId?: string
  ) {
  }

  private _stopped$ = new Subject<boolean>();

  get stopped$() {
    return this._stopped$
  }

  get send$() {
    return this._input$;
  }

  get receive$() {
    return this._receive$;
  }

  get key() {
    return this.connectionKey || '';
  }

  set key(key: string) {
    this.connectionKey = key;
  }

  static create(socketUrl: string, lastConnectionId?: string) {
    const input$ = new ReplaySubject<string>();
    const socket$ = RxjsWebsocketWrapper.makeWebSocketObservable<string>(socketUrl);
    const disconnect$ = new Subject<void>();
    const receive$ = socket$.pipe(
      takeUntil(disconnect$),
      switchMap((getResponses: GetWebSocketResponses<string>) => {
        return getResponses(input$)
      }),
      share()
    )
    return new WebsocketConnection(input$, receive$, disconnect$, lastConnectionId);
  }

  public stop(shouldReconnect = true) {
    this._stopped$.next(shouldReconnect);
    this._stopped$.complete();
    this._input$.complete();
  }

  public disconnect() {
    this._disconnect$.next();
    this._disconnect$.complete();
  }

  public startPing(
    pingTime = TIME_TO_PING,
    pongTime = TIME_TO_PONG,
    onPongTimeout?: () => void,
    onPongReceived?: () => void,
  ) {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    this._stopped$.subscribe(() => clearTimeout(timeout));
    this._receive$.pipe(
      takeUntil(this._stopped$),
      map(message => JSON.parse(message)),
      filter(message => message.type === 'pong'),
    ).subscribe(() => {
      clearTimeout(timeout);
      onPongReceived?.();
    })
    interval(pingTime)
      .pipe(
        takeUntil(this._stopped$),
        map(() => this._input$.next(JSON.stringify({type: 'ping'}))))
      .subscribe(() => {
        timeout = setTimeout(() => {
          onPongTimeout?.();
          this.stop();
        }, pongTime);
      })
  }
}
